import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, FileText, Briefcase, UsersRound, Mail, MessageSquare,
  User, FileDown, Settings, Moon, Sun, LogOut, Home, ChevronRight,
  ChevronsUpDown, Menu, X, ArrowUpRight, BarChart3, FolderKanban,
} from 'lucide-react';
import { AppShell } from '../../components/AppShell/AppShell';
import { Sidebar } from '../../components/Sidebar/Sidebar';
import { Topbar } from '../../components/Topbar/Topbar';
import { Avatar } from '../../components/Avatar/Avatar';
import { IconButton } from '../../components/IconButton/IconButton';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { cn } from '../../utils/cn';

/**
 * AdminAppShell — Sidebar + Topbar + main préconfigurés pour l'admin.
 *
 * Sidebar responsive : visible par défaut en ≥ lg, drawer animé en mobile.
 * Main full-width par défaut (back office), avec un padding intérieur léger.
 */

const NAV_ITEMS = [
  { key: 'dashboard',     label: 'Dashboard',       icon: LayoutDashboard, path: '/dashboard',                section: 'Pilotage' },
  { key: 'articles',      label: 'Articles',        icon: FileText,        path: '/dashboard/articles',       section: 'Pilotage', countKey: 'articles' },
  { key: 'analytics',     label: 'Analytics',       icon: BarChart3,       path: '/dashboard/analytics',      section: 'Pilotage' },
  { key: 'projects',      label: 'Projets',         icon: FolderKanban,    path: '/dashboard/projects',       section: 'Pilotage' },
  { key: 'stages',        label: 'Stages',          icon: Briefcase,       path: '/dashboard/stages',         section: 'Pilotage' },
  { key: 'professionals', label: 'Professionnels',  icon: UsersRound,      path: '/dashboard/professionals',  section: 'Pilotage' },
  { key: 'newsletter',    label: 'Newsletter',      icon: Mail,            path: '/dashboard/newsletter',     section: 'Communication' },
  { key: 'contacts',      label: 'Contacts',        icon: MessageSquare,   path: '/dashboard/contacts',       section: 'Communication', countKey: 'contacts', badgeVariant: 'new' },
  { key: 'users',         label: 'Utilisateurs',    icon: User,            path: '/dashboard/users',          section: 'Compte' },
  { key: 'cvs',           label: 'CV',              icon: FileDown,        path: '/dashboard/cvs',            section: 'Compte' },
  { key: 'profile',       label: 'Mon profil',      icon: Settings,        path: '/dashboard/profile',        section: 'Compte' },
];

function isPathActive(currentPath, itemPath) {
  if (itemPath === '/dashboard') return currentPath === '/dashboard' || currentPath === '/dashboard/';
  return currentPath.startsWith(itemPath);
}

export function AdminAppShell({
  user,
  currentPath = '',
  onLogout,
  isDarkMode,
  onToggleDarkMode,
  LinkComponent = 'a',
  topbarRight,
  topbarCenter,
  breadcrumb,
  pageTitle,
  counts = {},
  children,
  className,
  logoSrc = '/img/logo.png',
  brandName = 'Portfolio',
  signature,                  // { title, description, ghost, action } ou false pour cacher
}) {
  const Link = LinkComponent;
  const sections = ['Pilotage', 'Communication', 'Compte'];

  const [mobileOpen, setMobileOpen] = useState(false);
  // Fermer la sidebar mobile dès qu'on change de page
  useEffect(() => { setMobileOpen(false); }, [currentPath]);
  // Verrouille le scroll body quand drawer ouvert
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const brand = (
    <div className="flex items-center gap-2.5">
      {logoSrc ? (
        <span className="w-9 h-9 rounded-lg bg-cream flex items-center justify-center overflow-hidden shrink-0">
          <img src={logoSrc} alt={`${brandName} logo`} className="w-7 h-7 object-contain" />
        </span>
      ) : (
        <span className="w-9 h-9 rounded-lg bg-cream flex items-center justify-center text-ink font-bold text-sm">
          {brandName.charAt(0)}
        </span>
      )}
      <div className="leading-tight min-w-0">
        <div className="text-[15px] font-bold tracking-tight truncate">{brandName}<span className="font-serif italic font-normal align-baseline">.</span></div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-cream/55 mt-0.5">Admin</div>
      </div>
    </div>
  );

  const sidebarContent = (
    <>
      <Sidebar.Brand>
        <div className="flex items-center justify-between gap-2">
          {brand}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden w-8 h-8 rounded-md bg-transparent border-0 text-cream/70 hover:text-cream hover:bg-cream/10 inline-flex items-center justify-center cursor-pointer"
            aria-label="Fermer le menu"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>
      </Sidebar.Brand>

      <Sidebar.Nav>
        {sections.map((section) => (
          <Sidebar.Section key={section} label={`— ${section}`}>
            {NAV_ITEMS.filter((i) => i.section === section).map((item) => {
              const Icon = item.icon;
              const active = isPathActive(currentPath, item.path);
              const count = item.countKey ? counts[item.countKey] : undefined;
              const props = {
                as: Link,
                isActive: active,
                icon: <Icon />,
                label: item.label,
                onClick: () => setMobileOpen(false),
              };
              if (Link === 'a') props.href = item.path;
              else props.to = item.path;
              if (count != null && count > 0) {
                if (item.badgeVariant === 'new') {
                  props.badge = (
                    <span className="text-[10px] bg-cream text-ink px-1.5 py-0.5 rounded-full font-bold">{count}</span>
                  );
                } else {
                  props.count = count;
                }
              }
              return <Sidebar.NavItem key={item.key} {...props} />;
            })}
          </Sidebar.Section>
        ))}
      </Sidebar.Nav>

      {signature !== false && (() => {
        const firstName = user?.name ? user.name.split(' ')[0] : 'Toi';
        const articlesCount = counts?.articles;
        const sig = {
          title: signature?.title ?? `— ${firstName}`,
          description: signature?.description ?? "Écrire un peu chaque jour. Publier sans honte. Boucler avant lundi.",
          ghost: signature?.ghost ?? (articlesCount > 0 ? String(articlesCount) : null),
          action: signature?.action ?? (
            Link === 'a' ? (
              <a href="/dashboard/profile" className="inline-flex items-center gap-1.5">
                Profil <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
              </a>
            ) : (
              <Link to="/dashboard/profile" className="inline-flex items-center gap-1.5">
                Profil <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
              </Link>
            )
          ),
        };
        return (
          <Sidebar.SignatureBlock
            title={sig.title}
            description={sig.description}
            ghost={sig.ghost}
            action={sig.action}
          />
        );
      })()}

      <Sidebar.Footer>
        <div className="flex items-center gap-1 mb-2">
          <Tooltip content={isDarkMode ? 'Mode clair' : 'Mode sombre'}>
            <button
              type="button"
              onClick={onToggleDarkMode}
              className="w-9 h-9 rounded-md hover:bg-cream/10 flex items-center justify-center text-cream/80 hover:text-cream transition-colors"
              aria-label={isDarkMode ? 'Mode clair' : 'Mode sombre'}
            >
              {isDarkMode ? <Sun className="w-4 h-4" strokeWidth={1.8} /> : <Moon className="w-4 h-4" strokeWidth={1.8} />}
            </button>
          </Tooltip>
          {Link === 'a' ? (
            <Tooltip content="Retour au site">
              <a href="/" className="w-9 h-9 rounded-md hover:bg-cream/10 flex items-center justify-center text-cream/80 hover:text-cream transition-colors" aria-label="Retour au site">
                <Home className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </Tooltip>
          ) : (
            <Tooltip content="Retour au site">
              <Link to="/" className="w-9 h-9 rounded-md hover:bg-cream/10 flex items-center justify-center text-cream/80 hover:text-cream transition-colors" aria-label="Retour au site">
                <Home className="w-4 h-4" strokeWidth={1.8} />
              </Link>
            </Tooltip>
          )}
          {onLogout && (
            <Tooltip content="Déconnexion">
              <button
                type="button"
                onClick={onLogout}
                className="w-9 h-9 rounded-md hover:bg-danger/20 flex items-center justify-center text-cream/80 hover:text-cream transition-colors ml-auto"
                aria-label="Déconnexion"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </Tooltip>
          )}
        </div>
        <DropdownMenu
          side="top"
          align="start"
          triggerWrapperClassName="block"
          trigger={
            <button
              type="button"
              aria-label="Menu utilisateur"
              className="w-full rounded-md p-2 flex items-center gap-2.5 bg-cream/[0.06] hover:bg-cream/[0.12] transition-colors text-left"
            >
              <Avatar
                src={user?.avatarUrl}
                name={user?.name || user?.email || 'User'}
                size="sm"
                className="!bg-cream !text-ink shrink-0"
              />
              <div className="leading-tight min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold truncate text-cream">{user?.name || 'Utilisateur'}</div>
                <div className="text-[10.5px] text-cream/65 truncate">{user?.email || ''}</div>
              </div>
              <ChevronsUpDown className="w-3.5 h-3.5 text-cream/60 shrink-0" />
            </button>
          }
          className="!min-w-[240px]"
        >
          {({ close }) => (
            <>
              <DropdownMenu.Label>Connecté en tant que</DropdownMenu.Label>
              <div className="px-2.5 pb-1.5 pt-0.5">
                <div className="text-[12.5px] font-semibold text-text truncate">{user?.name || 'Utilisateur'}</div>
                <div className="text-[11px] text-mute truncate">{user?.email || ''}</div>
              </div>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                icon={<User strokeWidth={1.8} />}
                onSelect={() => {
                  close();
                  if (Link === 'a') {
                    window.location.href = '/dashboard/profile';
                  } else {
                    // Link de react-router — utilise history.push via window.location
                    // (DropdownMenu.Item ne supporte pas Link as, simple navigation)
                    window.location.href = '/dashboard/profile';
                  }
                }}
              >
                Mon profil
              </DropdownMenu.Item>
              <DropdownMenu.Item
                icon={isDarkMode ? <Sun strokeWidth={1.8} /> : <Moon strokeWidth={1.8} />}
                onSelect={() => { onToggleDarkMode?.(); close(); }}
              >
                {isDarkMode ? 'Mode clair' : 'Mode sombre'}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                icon={<Home strokeWidth={1.8} />}
                onSelect={() => { close(); window.location.href = '/'; }}
              >
                Retour au site
              </DropdownMenu.Item>
              {onLogout && (
                <>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item
                    danger
                    icon={<LogOut strokeWidth={1.8} />}
                    onSelect={() => { close(); onLogout(); }}
                  >
                    Déconnexion
                  </DropdownMenu.Item>
                </>
              )}
            </>
          )}
        </DropdownMenu>
      </Sidebar.Footer>
    </>
  );

  return (
    <div data-ds-theme="default" className={cn('h-screen flex flex-col', className)}>
      <AppShell>
        {/* Sidebar desktop : visible ≥ lg */}
        <div className="hidden lg:flex">
          <Sidebar>{sidebarContent}</Sidebar>
        </div>

        {/* Sidebar mobile : drawer absolu */}
        {mobileOpen && (
          <>
            <div
              aria-hidden
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 z-drawer bg-ink/40 backdrop-blur-[2px] animate-fade-in"
            />
            <div className="lg:hidden fixed top-0 left-0 bottom-0 z-drawer animate-slide-down"
                 style={{ animation: 'slide-in-left 220ms cubic-bezier(0.2,0,0,1.1)' }}>
              <Sidebar>{sidebarContent}</Sidebar>
              <style>{`
                @keyframes slide-in-left {
                  from { transform: translateX(-100%); opacity: 0.6; }
                  to   { transform: translateX(0);     opacity: 1;   }
                }
              `}</style>
            </div>
          </>
        )}

        <AppShell.Body>
          <Topbar>
            <Topbar.Left>
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden w-9 h-9 rounded-md hover:bg-bg-2 text-text inline-flex items-center justify-center mr-1"
                aria-label="Ouvrir le menu"
              >
                <Menu className="w-5 h-5" strokeWidth={1.8} />
              </button>
              {breadcrumb || (
                <div className="flex items-center gap-1.5 text-sm text-mute">
                  <span className="hidden sm:inline">Admin</span>
                  {pageTitle && (
                    <>
                      <ChevronRight className="hidden sm:inline w-3.5 h-3.5 text-mute-2" />
                      <span className="text-text font-semibold truncate">{pageTitle}</span>
                    </>
                  )}
                </div>
              )}
            </Topbar.Left>
            {topbarCenter && <Topbar.Center>{topbarCenter}</Topbar.Center>}
            <Topbar.Right>
              {topbarRight}
            </Topbar.Right>
          </Topbar>

          <AppShell.Main>
            {children}
          </AppShell.Main>
        </AppShell.Body>
      </AppShell>
    </div>
  );
}

export default AdminAppShell;
